/**
 * Plays one narrated section: the car drives the stretch of road the voice covered while the
 * browser speaks the opener and script. The clock is the utterance itself, so the car reaches
 * the end of the stretch when the voice stops, whatever pace the voice is set to.
 */
import { bearingDeg, bearingDiff, type SimNarration, type SimResult, type SimSample } from '@roadtrip/core';
import { playChime } from './chime';
import type { RouteMap } from './map';
import type { Speech } from './speech';

export class Playback {
  private raf = 0;
  private timer = 0;
  private playing: number | null = null;
  private sequence = false;

  constructor(
    private map: RouteMap,
    private speech: Speech,
    private rate: () => number,
    private chime: () => boolean,
    private onState: (i: number | null, note: string) => void,
    /** The phone's Now Playing arrow: rotated toward the landmark relative to the car's heading. */
    private arrow: HTMLElement,
  ) {}

  get current(): number | null {
    return this.playing;
  }

  play(r: SimResult, i: number, then?: () => void): void {
    const n = r.narrations[i];
    if (!n) return;
    this.cancelMotion();
    this.speech.stop();
    this.playing = i;
    this.map.fitSection(r, n);
    this.map.highlight(i);
    this.onState(i, `${i + 1} of ${r.narrations.length}: ${n.landmark.title}`);
    this.moveCar(n.startPos ?? n.triggerPos, n);
    // The cue plays first, as it will on the phone; Stop during the cue must still win.
    const cue = this.chime() ? playChime() : Promise.resolve();
    void cue.then(() => {
      if (this.playing === i) this.speakSection(r, n, i, then);
    });
  }

  private speakSection(r: SimResult, n: SimNarration, i: number, then?: () => void): void {
    const text = `${n.intro} ${n.landmark.script}`;
    const est = this.speech.estimateSec(text, this.rate());
    this.drive(r, n, est);
    this.speech.speak(text, this.rate(), () => {
      if (this.playing !== i) return;
      this.cancelMotion();
      const end = n.endPos ?? n.startPos;
      if (end) this.map.moveCar(end.lat, end.lon);
      this.arrow.hidden = true;
      this.playing = null;
      this.map.highlight(null);
      if (then) then();
      else this.onState(null, `Finished ${n.landmark.title}.`);
    });
  }

  playAll(r: SimResult): void {
    this.stop();
    this.sequence = true;
    let i = 0;
    const next = (): void => {
      if (!this.sequence || i >= r.narrations.length) {
        this.sequence = false;
        this.onState(null, i >= r.narrations.length ? `That is the whole drive: ${r.narrations.length} stories.` : 'Stopped.');
        return;
      }
      const idx = i++;
      this.play(r, idx, () => {
        // A short breath between stories; the road in between is skipped.
        this.timer = window.setTimeout(next, 900);
      });
    };
    next();
  }

  stop(): void {
    this.sequence = false;
    window.clearTimeout(this.timer);
    this.cancelMotion();
    this.speech.stop();
    this.playing = null;
    this.arrow.hidden = true;
    this.map.highlight(null);
    this.onState(null, 'Stopped.');
  }

  private moveCar(p: SimSample, n: SimNarration): void {
    this.map.moveCar(p.lat, p.lon);
    this.arrow.hidden = false;
    const rel = p.heading == null ? null : bearingDiff(p.heading, bearingDeg(p.lat, p.lon, n.landmark.lat, n.landmark.lon));
    this.arrow.classList.toggle('none', rel == null);
    this.arrow.style.setProperty('--rel', `${rel ?? 0}deg`);
    this.arrow.title = rel == null ? 'No heading yet' : `${Math.round(Math.abs(rel))}° ${rel < 0 ? 'left' : 'right'} of the heading (straight line)`;
  }

  private cancelMotion(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Move the car along the samples the voice covered, paced to the estimated speech length. */
  private drive(r: SimResult, n: SimNarration, estSec: number): void {
    const from = n.startPos?.t ?? n.startedAt;
    const to = n.endedAt ?? from + n.landmark.est_seconds;
    const samples = r.path.filter((p) => p.t >= from && p.t <= to);
    if (!samples.length) return;
    this.moveCar(samples[0]!, n);
    const t0 = performance.now();
    const step = (): void => {
      const f = Math.min(1, (performance.now() - t0) / 1000 / Math.max(1, estSec));
      const p = samples[Math.min(samples.length - 1, Math.floor(f * (samples.length - 1)))]!;
      this.moveCar(p, n);
      if (f < 1) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }
}
